import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import UnitConfirmDelete from "../../components/Property/UnitConfirmDelete";
import PropertyConfirmDelete from "../../components/Property/PropertyConfirmDelete";
import UnitFormModal from "../../components/Property/UnitFormModal";
import PropertyFormModal from "../../components/Property/PropertyFormModal";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
    padding: "1.5rem",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "1.25rem",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardBody: { padding: "1rem 1.125rem" },
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 6,
  },
  kpiValue: { fontSize: 20, fontWeight: 700, lineHeight: 1.2, color: "#1a1d23" },
  kpiSub: { fontSize: 12, color: "#9ca3af", marginTop: 5 },
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
      red: { background: "#FEF2F2", color: "#991B1B" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 14px",
    borderRadius: 6,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnDanger: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid #FECACA",
    background: "transparent",
    color: "#991B1B",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  th: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
    padding: "8px 12px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    background: "#fafafa",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1a1d23",
    fontSize: 13,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  spinner: {
    display: "inline-block",
    width: 18,
    height: 18,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 8,
  },
  errorBanner: {
    fontSize: 13,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: "1rem",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatCurrency = (value) => {
  if (value === undefined || value === null) return "N/A";
  return new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const statusPillVariant = (status) => {
  const map = {
    available: "green",
    pending: "amber",
    rented: "blue",
    active: "blue",
    withdrawn: "gray",
    inactive: "gray",
    under_renovation: "amber",
  };
  return map[status] || "gray";
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const PageSpinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4rem",
        fontSize: 14,
        color: "#6b7280",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <span style={s.spinner} />
      Loading property units…
    </div>
  </>
);

// ── Main component ────────────────────────────────────────────────────────────
function PropertyUnits() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [showCreateUnitModal, setShowCreateUnitModal] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [showDeleteBuildingModal, setShowDeleteBuildingModal] = useState(false);
  const [deletingBuilding, setDeletingBuilding] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const headers = {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        };
        const [propertyRes, unitsRes] = await Promise.all([
          fetch(API_ENDPOINTS.properties.getById(id), { headers }),
          fetch(API_ENDPOINTS.properties.getUnits(id), { headers }),
        ]);
        if (!propertyRes.ok) throw new Error("Unable to load property details");
        if (!unitsRes.ok) throw new Error("Unable to load units");
        const propertyData = await propertyRes.json();
        const unitsData = await unitsRes.json();
        setProperty(propertyData.data);
        setUnits(unitsData.data || []);
      } catch (err) {
        console.error("Error loading property units page:", err);
        setError(err.message || "Failed to load property units");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, getAuthHeaders]);

  useEffect(() => {
    if (!loading && property && property.type !== "multi_unit_building") {
      navigate(`/properties/propertyDetails/${property._id}`, { replace: true });
    }
  }, [loading, property, navigate]);

  const hasUnits = useMemo(() => units && units.length > 0, [units]);

  const handleShowUnit = (unitId) => navigate(`/properties/propertyDetails/${unitId}?fromBuilding=${id}`);
  const handleBackToProperties = () => navigate("/properties");
  const handleDeleteClick = (unit) => { setUnitToDelete(unit); setDeleteModalOpen(true); };

  const handleConfirmDelete = async (unitId) => {
    try {
      const response = await fetch(API_ENDPOINTS.units.delete(unitId), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      if (!response.ok) throw new Error("Failed to delete unit");
      setUnits(units.filter((unit) => unit._id !== unitId));
      setDeleteModalOpen(false);
      setUnitToDelete(null);
    } catch (err) {
      console.error("Error deleting unit:", err);
      setError(err.message || "Failed to delete unit");
      setDeleteModalOpen(false);
      setUnitToDelete(null);
    }
  };

  const handleUnitCreated = (newUnit, isCreate) => {
    if (isCreate) setUnits([...units, newUnit]);
    setShowCreateUnitModal(false);
  };

  const handleAddAnotherUnit = (newUnit) => { setUnits([...units, newUnit]); };

  const handleEditProperty = (updatedProperty) => {
    setProperty(updatedProperty);
    setShowEditPropertyModal(false);
  };

  const handleDeleteBuilding = async (propertyId) => {
    if (!propertyId) return;
    setDeletingBuilding(true);
    try {
      const response = await fetch(API_ENDPOINTS.properties.delete(propertyId), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      if (!response.ok) throw new Error("Failed to delete building");
      navigate("/properties");
    } catch (err) {
      console.error("Error deleting building:", err);
      setError(err.message || "Failed to delete building");
      setShowDeleteBuildingModal(false);
    } finally {
      setDeletingBuilding(false);
    }
  };

  if (loading) return <PageSpinner />;

  if (error) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={s.errorBanner}>{error}</div>
        <button style={s.btn} onClick={handleBackToProperties}>
          ← Back to Properties
        </button>
      </div>
    );
  }

  if (!property) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ ...s.errorBanner, background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}>
          Property not found.
        </div>
        <button style={s.btn} onClick={handleBackToProperties}>
          ← Back to Properties
        </button>
      </div>
    );
  }

  const availableCount = units.filter((u) => u.status === "available").length;

  return (
    <div style={s.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Page header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: "1.25rem",
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#1a1d23", marginBottom: 4 }}>
            {property.buildingName || property.title}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {[
              property.address?.street,
              property.address?.city,
              property.address?.parish,
              property.address?.zipCode,
            ]
              .filter(Boolean)
              .join(", ")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button style={s.btn} onClick={() => setShowEditPropertyModal(true)}>
            Edit Property
          </button>
          {units.length === 0 && (
            <button style={s.btnDanger} onClick={() => setShowDeleteBuildingModal(true)}>
              Delete Building
            </button>
          )}
          <button style={s.btn} onClick={handleBackToProperties}>
            ← Back to Properties
          </button>
          <Pill variant="blue">
            {units.length} {units.length === 1 ? "Unit" : "Units"}
          </Pill>
        </div>
      </div>

      {/* ── Building info strip ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 10,
          marginBottom: "1.25rem",
        }}
      >
        {/* Building summary */}
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Building summary</div>
          <div style={{ ...s.kpiValue, textTransform: "capitalize" }}>
            {property.status?.replace("_", " ") || "—"}
          </div>
          <div style={s.kpiSub}>
            {property.buildingDetails?.totalUnits || units.length} total units
          </div>
        </div>

        {/* Amenities */}
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Amenities</div>
          {property.amenities && property.amenities.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {property.amenities.map((amenity) => (
                <Pill key={amenity} variant="gray">
                  {amenity.replace("_", " ")}
                </Pill>
              ))}
            </div>
          ) : (
            <div style={s.kpiSub}>No shared amenities listed</div>
          )}
        </div>

        {/* Property owner */}
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Property owner</div>
          {property.propertyManager?.name ? (
            <>
              <div style={{ ...s.kpiValue, fontSize: 15 }}>{property.propertyManager.name}</div>
              {property.propertyManager.contact && (
                <div style={s.kpiSub}>{property.propertyManager.contact}</div>
              )}
              {property.propertyManager.email && (
                <div style={s.kpiSub}>{property.propertyManager.email}</div>
              )}
            </>
          ) : (
            <div style={s.kpiSub}>No property owner listed</div>
          )}
        </div>
      </div>

      {/* ── Units table ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <div>
            <span>Units</span>
            <span
              style={{ fontSize: 12, color: "#6b7280", fontWeight: 400, marginLeft: 8 }}
            >
              View and manage individual units for this building
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {hasUnits && (
              <Pill variant="green">{availableCount} Available</Pill>
            )}
            <button style={s.btnPrimary} onClick={() => setShowCreateUnitModal(true)}>
              + Add Unit
            </button>
          </div>
        </div>

        {hasUnits ? (
          <div style={{ overflowX: "auto" }}>
            <table className="re-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Unit", "Title", "Status", "Beds", "Baths", "Sq Ft", "Price", "Updated", ""].map(
                    (h, i) => (
                      <th key={i} style={{ ...s.th, textAlign: i === 8 ? "right" : "left" }}>
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {units.map((unit) => (
                  <tr key={unit._id}>
                    <td data-label="Unit" data-stacked="true" style={{ ...s.td, fontWeight: 600 }}>{unit.unitNumber || "N/A"}</td>
                    <td data-label="Title" data-stacked="true" style={s.td}>{unit.title || "—"}</td>
                    <td data-label="Status" data-stacked="true" style={s.td}>
                      <Pill variant={statusPillVariant(unit.status)}>
                        {unit.status?.replace("_", " ") || "unknown"}
                      </Pill>
                    </td>
                    <td data-label="Beds" data-stacked="true" style={s.td}>{unit.propertyDetails?.bedrooms ?? "—"}</td>
                    <td data-label="Baths" data-stacked="true" style={s.td}>{unit.propertyDetails?.bathrooms ?? "—"}</td>
                    <td data-label="Sq Ft" data-stacked="true" style={s.td}>{unit.propertyDetails?.squareFootage ?? "—"}</td>
                    <td data-label="Price" data-stacked="true" style={{ ...s.td, fontWeight: 600 }}>
                      {unit.type === "sale"
                        ? formatCurrency(unit.pricing?.salePrice)
                        : formatCurrency(unit.pricing?.rent)}
                      <span style={{ display: "block", fontSize: 10, fontWeight: 400, color: "#9ca3af", marginTop: 1 }}>
                        {unit.type === "sale" ? "sale" : "/ mo"}
                      </span>
                    </td>
                    <td data-label="Updated" data-stacked="true" style={{ ...s.td, color: "#6b7280" }}>
                      {unit.updatedAt ? new Date(unit.updatedAt).toLocaleDateString() : "N/A"}
                    </td>
                    <td data-label="" style={{ ...s.td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button style={s.btnPrimary} onClick={() => handleShowUnit(unit._id)}>
                          Show
                        </button>
                        <button style={s.btnDanger} onClick={() => handleDeleteClick(unit)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: "2rem", textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
            No units found for this building.
          </div>
        )}
      </div>

      {/* ── External modal components ── */}
      <UnitConfirmDelete
        show={deleteModalOpen}
        onHide={() => { setDeleteModalOpen(false); setUnitToDelete(null); }}
        unit={unitToDelete}
        onConfirmDelete={handleConfirmDelete}
      />

      <UnitFormModal
        show={showCreateUnitModal}
        onHide={() => setShowCreateUnitModal(false)}
        unit={null}
        propertyId={id}
        onUnitUpdated={handleUnitCreated}
        mode="create"
        onAddAnother={handleAddAnotherUnit}
      />

      <PropertyFormModal
        show={showEditPropertyModal}
        onHide={() => setShowEditPropertyModal(false)}
        property={property}
        onPropertyUpdated={handleEditProperty}
        mode="edit"
      />

      <PropertyConfirmDelete
        show={showDeleteBuildingModal}
        onHide={() => setShowDeleteBuildingModal(false)}
        property={property}
        onConfirmDelete={handleDeleteBuilding}
      />
    </div>
  );
}

export default PropertyUnits;
